import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useCustomersInfiniteQuery,
    useDeleteCustomersMutation,
} from './useCustomersQuery';
import {useCustomersSearch} from './useCustomersSearch';
import {useCustomersTableColumns} from './useCustomersTableColumns';
import {CustomersFilters} from './useCustomersFilter';
import {Customer} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created` is set by Stripe at
 * creation and never moves, so it satisfies both — unlike `updated`, which
 * would reorder the list under an operator who is reading it. It is also the
 * order the API returns (Stripe lists customers newest-first), so the first
 * paint AGREES with the server rather than reshuffling it.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Customer> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * customer id (`cus_...`) is what an operator pastes in from the Stripe
 * dashboard when chasing a specific account.
 */
const matchesKeyword = (item: Customer, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.name,
        item.description,
        item.email,
        item.phone,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: Customer, filters: CustomersFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof Customer];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listCustomers($limit, $nextToken)` accepts no filter and no sort argument,
 * so the server can neither narrow nor order the result set. Everything below
 * therefore operates on the loaded window only. Sorting page 1 does not sort
 * the dataset, and a keyword matches only rows the browser has already
 * received — the other rows were never examined.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 *
 * Fixing it properly means a server-side filter/sort argument on the API — a
 * backend change, not a footer fix.
 */
export const useCustomers = (filters: CustomersFilters = {}) => {
    const query = useCustomersInfiniteQuery();
    const {columns} = useCustomersTableColumns();
    const {keyword} = useCustomersSearch();
    const deleteMutation = useDeleteCustomersMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: Customer[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-seconds into millis ONCE, here, so the
        // Created column can both render and sort the same value.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<Customer>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
